import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import {
  ACTIVITY_CREATE_STATE_OPTIONS,
  ACTIVITY_STATUS_LABELS,
  ACTIVITY_TYPE_MAP,
  ACTIVITY_TYPE_OPTIONS,
  BADGE_SX,
  SURFACE_SX,
  formatAssignableUserLabel,
  formatDate,
  getActivityAuditLine,
} from './customerDetailUtils';

const FORM_CONTAINER_SX = {
  p: 1.5,
  borderRadius: 3,
  border: '1px solid rgba(0,0,0,0.07)',
  backgroundColor: '#fafcfc',
};

const CustomerDetailActivitiesSection = memo(function CustomerDetailActivitiesSection({
  assigneeOptions,
  assignableUsersError,
  contactOptions,
  error,
  formMode,
  formValues,
  isLoadingAssignableUsers,
  isSaving,
  items,
  onAssigneeChange,
  onAssigneeInputChange,
  onCancel,
  onFieldChange,
  onStartCreate,
  onStartEdit,
  onStatusAction,
  onSubmit,
  selectedAssignee,
}) {
  return (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Open activities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Planned CRM work stays here until it is marked done or cancelled.
            </Typography>
          </Box>

          <Button
            type="button"
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon fontSize="small" />}
            onClick={onStartCreate}
            sx={{ textTransform: 'none' }}
          >
            Add activity
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        {formMode && (
          <Box component="form" onSubmit={onSubmit} sx={FORM_CONTAINER_SX}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={700}>
                {formMode === 'create' ? 'Add activity' : 'Edit activity'}
              </Typography>

              {assignableUsersError && <Alert severity="warning">{assignableUsersError}</Alert>}

              {formValues.state === 'DONE' && (
                <Alert severity="info">
                  This logs completed work directly to the customer timeline. Due date is not required.
                </Alert>
              )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.25,
                }}
              >
                <TextField
                  id="activity-type"
                  select
                  label="Activity type"
                  value={formValues.activityType}
                  onChange={onFieldChange('activityType')}
                  size="small"
                  fullWidth
                  required
                  SelectProps={{ native: true }}
                >
                  {ACTIVITY_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
                <TextField
                  id="activity-state"
                  select
                  label="Activity state"
                  value={formValues.state}
                  onChange={onFieldChange('state')}
                  size="small"
                  fullWidth
                  disabled={formMode === 'edit'}
                  required
                  SelectProps={{ native: true }}
                >
                  {ACTIVITY_CREATE_STATE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
                <TextField
                  label="Activity title"
                  value={formValues.title}
                  onChange={onFieldChange('title')}
                  size="small"
                  fullWidth
                  required
                />
                {formValues.state === 'OPEN' ? (
                  <TextField
                    label="Due date"
                    type="date"
                    value={formValues.dueDate}
                    onChange={onFieldChange('dueDate')}
                    size="small"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                ) : (
                  <TextField
                    label="Occurred at"
                    type="datetime-local"
                    value={formValues.occurredAt}
                    onChange={onFieldChange('occurredAt')}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                <Autocomplete
                  options={assigneeOptions}
                  value={selectedAssignee}
                  loading={isLoadingAssignableUsers}
                  onInputChange={onAssigneeInputChange}
                  onChange={onAssigneeChange}
                  getOptionLabel={formatAssignableUserLabel}
                  isOptionEqualToValue={(option, value) =>
                    Number(option?.id ?? 0) === Number(value?.id ?? 0)
                  }
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assignee"
                      placeholder="Search teammate"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingAssignableUsers ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <TextField
                  select
                  label="Activity contact"
                  value={formValues.contactId}
                  onChange={onFieldChange('contactId')}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">No linked contact</MenuItem>
                  {contactOptions.map((contact) => (
                    <MenuItem key={contact.id} value={String(contact.id)}>
                      {contact.name}
                      {!contact.active ? ' (Archived)' : ''}
                      {contact.isPrimary ? ' • Primary' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <TextField
                label="Activity note"
                value={formValues.note}
                onChange={onFieldChange('note')}
                size="small"
                multiline
                minRows={3}
                fullWidth
              />

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSaving}
                  sx={{ textTransform: 'none' }}
                >
                  {isSaving
                    ? 'Saving...'
                    : formMode === 'create'
                      ? 'Create activity'
                      : 'Save activity'}
                </Button>
                <Button type="button" variant="text" onClick={onCancel} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No open activities for this account.
          </Typography>
        ) : (
          <Stack spacing={1.15}>
            {items.map((activity) => {
              const activityType =
                ACTIVITY_TYPE_MAP.get(activity?.activityType) ?? ACTIVITY_TYPE_MAP.get('OTHER');

              return (
                <Box
                  key={activity.id}
                  sx={{
                    p: 1.4,
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: '#fcfdfd',
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {activity.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Due {formatDate(activity.dueDate)}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Box
                          component="span"
                          sx={{
                            ...BADGE_SX,
                            color: activityType.color,
                            backgroundColor: activityType.background,
                            borderColor: activityType.border,
                          }}
                        >
                          {activityType.label}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            ...BADGE_SX,
                            color: activity.assignedUser ? '#00695c' : 'text.secondary',
                            backgroundColor: activity.assignedUser
                              ? 'rgba(0,105,92,0.08)'
                              : 'rgba(0,0,0,0.04)',
                            borderColor: activity.assignedUser
                              ? 'rgba(0,105,92,0.16)'
                              : 'rgba(0,0,0,0.08)',
                          }}
                        >
                          {activity.assignedUser?.displayName || 'Unassigned'}
                        </Box>
                        {activity.contact && (
                          <Box
                            component="span"
                            sx={{
                              ...BADGE_SX,
                              color: activity.contact.active ? '#0d47a1' : 'text.secondary',
                              backgroundColor: activity.contact.active
                                ? 'rgba(13,71,161,0.08)'
                                : 'rgba(0,0,0,0.04)',
                              borderColor: activity.contact.active
                                ? 'rgba(13,71,161,0.16)'
                                : 'rgba(0,0,0,0.08)',
                            }}
                          >
                            {activity.contact.name}
                            {!activity.contact.active ? ' (Archived)' : ''}
                            {activity.contact.isPrimary ? ' • Primary' : ''}
                          </Box>
                        )}
                      </Stack>
                    </Stack>

                    {activity.note && (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {activity.note}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      {getActivityAuditLine(activity)}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        type="button"
                        size="small"
                        variant="text"
                        startIcon={<EditRoundedIcon fontSize="small" />}
                        onClick={() => onStartEdit(activity)}
                        disabled={isSaving}
                        sx={{ textTransform: 'none' }}
                      >
                        Edit {activity.title}
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        startIcon={<CheckCircleRoundedIcon fontSize="small" />}
                        onClick={() => onStatusAction(activity, 'DONE')}
                        disabled={isSaving}
                        sx={{ textTransform: 'none' }}
                      >
                        Mark done
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        color="inherit"
                        startIcon={<CancelRoundedIcon fontSize="small" />}
                        onClick={() => onStatusAction(activity, 'CANCELLED')}
                        disabled={isSaving}
                        sx={{ textTransform: 'none' }}
                      >
                        Cancel activity
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}

        {!formMode && items.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {ACTIVITY_STATUS_LABELS.DONE} items move into the timeline and {ACTIVITY_STATUS_LABELS.CANCELLED.toLowerCase()} items stay out of the default workflow.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
});

export default CustomerDetailActivitiesSection;
